import React from "react";
import { useState, useContext } from "react";
import { styled, useTheme } from "@mui/material/styles";
import { Outlet, useLocation, Link } from "react-router-dom";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Drawer from "@mui/material/Drawer";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import {
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  Typography,
  Badge,
  Paper
} from "@mui/material";

// Icons
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WifiIcon from "@mui/icons-material/Wifi";
import SecurityIcon from "@mui/icons-material/Security";
import FeedbackIcon from "@mui/icons-material/Feedback";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";

import { tokens, ColorModeContext } from "../../theme";
import Logo from "../../assets/image/logo_ceklongwis.png";
import { useAuth } from "../../auth/auth_provider";

const drawerWidth = 250;
const iconMenuSize = 20;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  //   backgroundColor: "black",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerMini = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const Item = ({ title, to, icon, selected, setSelected, isOpen, badge }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSelected = selected === to;
  
  return (
    <ListItem
      key={title.replace(/\W+/g, "_").toLowerCase()}
      disablePadding
      sx={{ display: "block", mb: 0.5 }}
    >
      <ListItemButton
        sx={{
          minHeight: 48,
          justifyContent: isOpen ? "flex-start" : "center",
          px: 2.5,
          mx: isOpen ? 2 : 1,
          borderRadius: 3,
          position: 'relative',
          background: isSelected 
            ? 'linear-gradient(135deg, #1dc39f 0%, #16a085 100%)' 
            : 'transparent',
          color: isSelected ? 'white' : '#2c3e50',
          boxShadow: isSelected ? '0 4px 12px rgba(29, 195, 159, 0.3)' : 'none',
          transition: 'all 0.3s ease',
          "&:hover": {
            background: isSelected 
              ? 'linear-gradient(135deg, #16a085 0%, #1dc39f 100%)'
              : 'rgba(29, 195, 159, 0.1)',
            transform: 'translateY(-1px)',
            boxShadow: isSelected 
              ? '0 6px 16px rgba(29, 195, 159, 0.4)'
              : '0 2px 8px rgba(29, 195, 159, 0.2)',
          },
        }}
        component={Link}
        to={to}
        onClick={() => setSelected(to)}
      >
        <ListItemIcon
          sx={{
            minWidth: 40,
            justifyContent: "center",
            color: isSelected ? 'white' : '#1dc39f',
          }}
        >
          {badge ? (
            <Badge 
              badgeContent={badge} 
              color="error" 
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '10px',
                  height: '16px',
                  minWidth: '16px'
                }
              }}
            >
              {icon}
            </Badge>
          ) : (
            icon
          )}
        </ListItemIcon>
        
        <ListItemText
          primary={title}
          sx={{
            opacity: isOpen ? 1 : 0,
            ml: isOpen ? 0 : -3,
            transition: 'all 0.3s ease',
          }}
          primaryTypographyProps={{
            fontWeight: isSelected ? 600 : 500,
            fontSize: "14px",
            color: isSelected ? 'white' : '#2c3e50'
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

const SideMenu = (props) => {
  let location = useLocation();
  const { user, logout } = useAuth();
  // Give us meaningful document titles for popping back/forward more than 1 entry
  const [selected, setSelected] = useState("Dashboard");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = Boolean(anchorEl);
  const [openListMenu, setOpenListMenu] = useState(false);
  const [pathaurl, setPathUrl] = useState('');
  const handleListMenu = () => {
    setOpenListMenu(!openListMenu);
  };

  const handleClickMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  React.useEffect(() => {
    document.title = location.pathname.slice(7);
    setSelected(location.pathname.slice(7));
    setPathUrl(location.pathname)
  }, [location, setSelected, setPathUrl]);

  const { window } = props;

  const theme = useTheme();
  const [open, setOpen] = React.useState(true);
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const contain =
    window !== undefined ? () => window().document.body : undefined;

  const drawer = (
    <Box
      sx={{
        height: '100%',
        background: 'linear-gradient(180deg, #f8fffe 0%, #e8f5e8 100%)',
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
        borderRight: '1px solid rgba(29, 195, 159, 0.1)',
      }}
    >
      <DrawerHeader>
        <Grid
          sx={{ flexGrow: 1, margin: 2 }}
          component={"div"}
          container
          justifyContent="center"
          alignItems="center"
        >
          {open ? (
            <Box sx={{ textAlign: 'center' }}>
              <img src={Logo} alt="logo" width={"160px"} />
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 1, 
                  color: '#1dc39f', 
                  fontWeight: 600,
                  fontSize: '12px'
                }}
              >
                Sistem Cek Longwis
              </Typography>
            </Box>
          ) : (
            <img src={Logo} alt="logo" width={"40px"} />
          )}
        </Grid>
      </DrawerHeader>
      
      <Divider sx={{ borderColor: 'rgba(29, 195, 159, 0.2)' }} />
      
      <List sx={{ px: 1, py: 2 }}>
        <Item
          title="Dashboard"
          to="dashboard"
          icon={<GridViewRoundedIcon sx={{ fontSize: iconMenuSize }} />}
          selected={selected}
          setSelected={setSelected}
          isOpen={open}
        />
        
        <Box sx={{ my: 2 }}>
          <Divider sx={{ borderColor: 'rgba(29, 195, 159, 0.2)' }} />
          {open && (
            <Typography 
              variant="caption" 
              sx={{ 
                px: 3, 
                py: 1, 
                color: '#7f8c8d', 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '11px'
              }}
            >
              Data Longwis
            </Typography>
          )}
        </Box>
        
        <Item
          title="Longwis Lokasi"
          to="rekapan"
          icon={<LocationOnIcon sx={{ fontSize: iconMenuSize }} />}
          selected={selected}
          setSelected={setSelected}
          isOpen={open}
        />
        <Item
          title="Longwis Internet"
          to="internet"
          icon={<WifiIcon sx={{ fontSize: iconMenuSize }} />}
          selected={selected}
          setSelected={setSelected}
          isOpen={open}
        />
        <Item
          title="Monitoring CCTV"
          to="monitoring-cctv"
          icon={<SecurityIcon sx={{ fontSize: iconMenuSize }} />}
          selected={selected}
          setSelected={setSelected}
          isOpen={open}
        />
        <Item
          title="Keluhan"
          to="keluhan"
          icon={<FeedbackIcon sx={{ fontSize: iconMenuSize }} />}
          selected={selected}
          setSelected={setSelected}
          isOpen={open}
        />
        
        <Box sx={{ my: 2 }}>
          <Divider sx={{ borderColor: 'rgba(29, 195, 159, 0.2)' }} />
          {open && (
            <Typography 
              variant="caption" 
              sx={{ 
                px: 3, 
                py: 1, 
                color: '#7f8c8d', 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '11px'
              }}
            >
              Manajemen User
            </Typography>
          )}
        </Box>
        
        <Item
          title="Daftar PIC"
          to="daftarpic"
          icon={<PersonIcon sx={{ fontSize: iconMenuSize }} />}
          selected={selected}
          setSelected={setSelected}
          isOpen={open}
        />
        <Item
          title="Daftar Admin"
          to="daftaruser"
          icon={<AdminPanelSettingsIcon sx={{ fontSize: iconMenuSize }} />}
          selected={selected}
          setSelected={setSelected}
          isOpen={open}
        />
      </List>
      
      {/* User Info at Bottom */}
      {open && user && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 20,
            left: 16,
            right: 16,
          }}
        >
          <Paper
            elevation={2}
            sx={{
              p: 2,
              background: 'linear-gradient(135deg, rgba(29, 195, 159, 0.1) 0%, rgba(143, 249, 171, 0.1) 100%)',
              border: '1px solid rgba(29, 195, 159, 0.2)',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#1dc39f' }}>
                <img src={Logo} alt="avatar" width={"100%"} />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#2c3e50',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {user?.username || 'Admin'}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#7f8c8d',
                    fontSize: '10px'
                  }}
                >
                  Administrator
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );

  const matches = useMediaQuery(theme.breakpoints.up("sm"));

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        {/* for styling topbar */}
        <Toolbar
          sx={{
            background: 'linear-gradient(90deg, #1dc39f 0%, #8ff9ab 100%)',
            boxShadow: '0 2px 10px rgba(29, 195, 159, 0.3)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Grid
            container
            justifyContent={"space-between"}
            alignItems={"center"}
            width={"100%"}
          >
            <Grid item container alignItems={"center"} xs={4}>
              <IconButton
                color="inherit"
                aria-label="toggle drawer"
                onClick={open ? handleDrawerClose : handleDrawerOpen}
                edge="start"
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                {open ? <ChevronLeftIcon /> : <MenuIcon />}
              </IconButton>
            </Grid>

            {/* Center Title */}
            <Grid 
              item 
              xs={4} 
              sx={{ 
                textAlign: 'center',
                display: { xs: 'none', md: 'block' }
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  fontSize: { xs: '16px', md: '18px' }
                }}
              >
                Sistem Cek Longwis Makassar
              </Typography>
            </Grid>

            {/* Right Side - User Menu */}
            <Grid item container alignItems={"center"} justifyContent="flex-end" xs={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* User Welcome Text */}
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'white',
                      fontWeight: 500,
                      textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}
                  >
                    Selamat datang, {user?.username || 'Admin'}
                  </Typography>
                </Box>

                {/* User Avatar Menu */}
                <Tooltip title="Account settings">
                  <IconButton
                    onClick={handleClickMenu}
                    size="small"
                    sx={{
                      ml: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                    aria-controls={openMenu ? "account-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={openMenu ? "true" : undefined}
                  >
                    <Avatar 
                      sx={{ 
                        width: 36, 
                        height: 36,
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          border: '2px solid rgba(255, 255, 255, 0.5)',
                        }
                      }}
                    >
                      <img src={Logo} alt="avatar" width={"100%"} />
                    </Avatar>
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={openMenu}
                onClose={handleCloseMenu}
                onClick={handleCloseMenu}
                PaperProps={{
                  elevation: 8,
                  sx: {
                    overflow: "visible",
                    filter: "drop-shadow(0px 4px 20px rgba(0,0,0,0.15))",
                    mt: 1.5,
                    borderRadius: 2,
                    minWidth: 200,
                    "& .MuiAvatar-root": {
                      width: 28,
                      height: 28,
                      ml: -0.5,
                      mr: 1,
                    },
                    "&::before": {
                      content: '""',
                      display: "block",
                      position: "absolute",
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: "background.paper",
                      transform: "translateY(-50%) rotate(45deg)",
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem 
                  onClick={colorMode.toggleColorMode}
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(29, 195, 159, 0.1)',
                    }
                  }}
                >
                  <ListItemIcon>
                    {theme.palette.mode === "dark" ? (
                      <DarkModeOutlinedIcon sx={{ color: '#1dc39f' }} />
                    ) : (
                      <LightModeOutlinedIcon sx={{ color: '#1dc39f' }} />
                    )}
                  </ListItemIcon>
                  <Typography variant="body2" fontWeight={500}>
                    {theme.palette.mode === "dark" ? "Mode Gelap" : "Mode Terang"}
                  </Typography>
                </MenuItem>
                
                <Divider />
                
                <MenuItem 
                  onClick={logout}
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    }
                  }}
                >
                  <ListItemIcon>
                    <LogoutRoundedIcon sx={{ color: '#f44336' }} />
                  </ListItemIcon>
                  <Typography variant="body2" fontWeight={500} color="#f44336">
                    Logout
                  </Typography>
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <Drawer
        container={contain}
        variant="temporary"
        open={!matches ? open : false}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
          },
          overflow: "auto",
        }}
      >
        {/* <Box sx={{
                    height: '100vh'
                }}> */}
        {drawer}
        {/* </Box> */}
      </Drawer>
      <DrawerMini
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
        }}
        open={open}
      >
        {drawer}
      </DrawerMini>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: "100%",
          maxHeight: "100vh",
          overflow: "auto",
        }}
      >
        <DrawerHeader />
        <Grid>
          <Outlet />
        </Grid>
      </Box>
    </Box>
  );
};

export default SideMenu;
